<template>
  <transition name="modal">
    <div id="addressModal" class="modal-mask tmsModalMask">
      <div class="modal-wrapper addressModalWrapper">
        <div>
          <div class="modal-header">
            <slot name="header">
              <h5 id="newCustomerAddressModelLabel" class="modal-title">
                {{ title }}
              </h5>
              <button
                v-if="!disableClose"
                id="btnX"
                type="button"
                class="close"
                @click="$emit('close')"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </slot>
          </div>
          <div class="modal-body">
            <div v-if="showName">
              <slot id="subTitle" name="body">
                <p>Fill out the form and click submit to save a customer to your address book</p>
              </slot>
            </div>
            <slot name="body">
              <div v-if="showName">
                <b-form-group
                  id="CustomerNameGroup"
                  label="Customer"
                  label-for="CustomerNameInput"
                  label-class="required"
                >
                  <b-form-input
                    id="CustomerNameInput"
                    v-model="CustomerAddressDto.CustomerName"
                    v-validate="{required: true, max:30}"
                    type="text"
                    :state="validateState('CustomerAddressDto.CustomerName')"
                    aria-describedby="CustomerNameLiveFeedback"
                    :class="{ 'is-invalid': errors.has('CustomerName') }"
                    size="sm"
                    name="CustomerName"
                  />
                  <b-form-invalid-feedback
                    id="CustomerNameLiveFeedback"
                  >
                    {{ errors.first("CustomerName") }}
                  </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group
                  id="CustomerName2Group"
                  label="Attn"
                  label-for="CustomerName2Input"
                >
                  <b-form-input
                    id="CustomerName2Input"
                    v-model="CustomerAddressDto.CustomerName2"
                    v-validate="{max:30}"
                    type="text"
                    :state="validateState('CustomerAddressDto.CustomerName2')"
                    aria-describedby="CustomerName2LiveFeedback"
                    :class="{ 'is-invalid': errors.has('CustomerName2') }"
                    size="sm"
                    name="CustomerName2"
                  />
                  <b-form-invalid-feedback id="CustomerName2LiveFeedback">
                    {{ errors.first("CustomerName2") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </div>

              <b-form-group
                id="Address1Group"
                label="Address Line 1"
                label-for="Address1Input"
                label-class="required"
              >
                <b-form-input
                  id="Address1Input"
                  v-model="CustomerAddressDto.AddressLine1"
                  v-validate="{required: true, max:30}"
                  type="text"
                  :state="validateState('CustomerAddressDto.AddressLine1')"
                  aria-describedby="Address1LiveFeedback"
                  :class="{ 'is-invalid': errors.has('Address1') }"
                  size="sm"
                  name="Address1"
                />
                <b-form-invalid-feedback id="Address1LiveFeedback">
                  {{ errors.first("Address1") }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group
                id="Address2Group"
                label="Address Line 2"
                label-for="Address2Input"
              >
                <b-form-input
                  id="Address2Input"
                  v-model="CustomerAddressDto.AddressLine2"
                  v-validate="{max:30}"
                  type="text"
                  :state="validateState('CustomerAddressDto.AddressLine2')"
                  aria-describedby="Address2LiveFeedback"
                  :class="{ 'is-invalid': errors.has('Address2') }"
                  size="sm"
                  name="Address2"
                />
                <b-form-invalid-feedback id="Address2LiveFeedback">
                  {{ errors.first("Address2") }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group
                id="CityGroup"
                label="City"
                label-for="CityInput"
                label-class="required"
              >
                <b-form-input
                  id="CityInput"
                  v-model="CustomerAddressDto.City"
                  v-validate="{required: true, max:35}"
                  type="text"
                  :state="validateState('CustomerAddressDto.City')"
                  aria-describedby="CityLiveFeedback"
                  :class="{ 'is-invalid': errors.has('City') }"
                  size="sm"
                  name="City"
                />
                <b-form-invalid-feedback id="CityLiveFeedback">
                  {{ errors.first("City") }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group
                id="CountryGroup"
                label="Country"
                label-for="CountryInput"
                label-class="required"
              >
                <v-select
                  id="country"
                  v-model="selectedCountry"
                  v-validate="'required'"
                  :class="{ 'is-invalid': errors.has('Country') }"
                  :options="country"
                  :select-on-tab="true"
                  name="Country"
                />
                <span
                  v-show="errors.has('Country')"
                  class="invalid-element"
                >{{ errors.first("Country") }}</span>
              </b-form-group>

              <b-form-group
                id="StateGroup"
                label="State"
                label-for="StateInput"
                :label-class="stateValidation.required ? 'required' : ''"
              >
                <b-form-input
                  id="StateInput"
                  v-model="CustomerAddressDto.StateProvince"
                  v-validate="stateValidation"
                  type="text"
                  :state="validateState('CustomerAddressDto.StateProvince')"
                  aria-describedby="StateLiveFeedback"
                  :class="{ 'is-invalid': errors.has('State') }"
                  size="sm"
                  name="State"
                />
                <b-form-invalid-feedback id="StateLiveFeedback">
                  {{ errors.first("State") }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group
                id="PostalCodeGroup"
                label="PostalCode"
                label-for="PostalCodeInput"
                :label-class="zipValidation.required ? 'required' : ''"
              >
                <b-form-input
                  id="PostalCodeInput"
                  v-model="CustomerAddressDto.PostalCode"
                  v-validate="zipValidation"
                  type="text"
                  :state="validateState('CustomerAddressDto.PostalCode')"
                  aria-describedby="PostalCodeLiveFeedback"
                  :class="{ 'is-invalid': errors.has('PostalCode') }"
                  size="sm"
                  name="PostalCode"
                />
                <b-form-invalid-feedback
                  id="PostalCodeLiveFeedback"
                >
                  {{ errors.first("PostalCode") }}
                </b-form-invalid-feedback>
              </b-form-group>
            </slot>
          </div>
          <div class="modal-footer">
            <DnFunctionButton
              button-id="saveAddress"
              :callback-fn="saveCustomerAddress"
              :text="saveButtonText"
              :size="smallSize"
            />
            <DnFunctionButton
              v-if="!disableClose"
              button-id="btnClose"
              :callback-fn="closeModal"
              :text="getContentItem('close')"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
  import { mapGetters } from "vuex";
  import vSelect from "vue-select";
  import { countryHelper } from "@/components/mixins/countryHelper";
  import { sizeHelper } from "@/components/mixins/sizeHelper";
  import DnFunctionButton from "@/components/Elements/DnFunctionButton";

  export default {
    components: {
      vSelect,
      DnFunctionButton,
    },
    mixins: [countryHelper, sizeHelper],
    props: {
      customerAddressDtoProp: {
        type: Object,
        default: () => ({}),
      },
      selectedCountryProp: {
        type: Object,
        default: () => ({}),
      },
      title: {
        type: String,
        required: false,
        default: "Address",
      },
      saveButtonText: {
        type: String,
        required: false,
        default: "Save",
      },
      showName: {
        type: Boolean,
        required: false,
        default: true,
      },
      disableClose: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    data: () => ({
      CustomerAddressDto: {
        AddressId: 0,
        CustomerId: 0,
        CustomerName: "",
        CustomerName2: "",
        AddressLine1: "",
        AddressLine2: "",
        City: "",
        StateProvince: "",
        Country: "",
        PostalCode: "",
      },
      selectedCountry: undefined,
    }),
    watch: {
      "selectedCountry": function() {
        this.addressUpdated();
      },
      "CustomerAddressDto": {
        handler: function() {
          this.addressUpdated();
        },
        deep: true
      },
    },
    computed: {
      ...mapGetters(["getContentItem"]),
      stateValidation: function() {
        if (this.selectedCountry && this.selectedCountry.id == "US") {
          return { required: true, max: 3 };
        }
        return { max: 3 };
      },
      zipValidation: function() {
        if (this.selectedCountry && this.selectedCountry.id == "US") {
          return { required: true, max: 10 };
        }
        return { max: 10 };
      },
    },
    beforeMount: function() {
      if (!_.isEmpty(this.customerAddressDtoProp)) {
        this.CustomerAddressDto = { ...this.customerAddressDtoProp };
        this.trimAddressData();
        if (this.selectedCountryProp) {
          this.selectedCountry = { ...this.selectedCountryProp };
        }
      }
    },
    methods: {
      closeModal: function() {
        this.$emit("close");
      },
      saveCustomerAddress: function() {
        this.trimAddressData();
        this.$validator.validateAll().then((valid) => {
          if (valid) {
            this.CustomerAddressDto.Country = this.selectedCountry.id;
            this.$emit("save", this.CustomerAddressDto);
          }
        });
      },
      addressUpdated() {
        this.CustomerAddressDto.Country = this.selectedCountry?.id ?? "";
        this.$emit("addressUpdated", this.CustomerAddressDto);
      },
      trimAddressData: function() {
        this.CustomerAddressDto.CustomerName = this.trimValue(this.CustomerAddressDto.CustomerName);
        this.CustomerAddressDto.CustomerName2 = this.trimValue(this.CustomerAddressDto.CustomerName2);
        this.CustomerAddressDto.AddressLine1 = this.trimValue(this.CustomerAddressDto.AddressLine1);
        this.CustomerAddressDto.AddressLine2 = this.trimValue(this.CustomerAddressDto.AddressLine2);
        this.CustomerAddressDto.City = this.trimValue(this.CustomerAddressDto.City);
        this.CustomerAddressDto.StateProvince = this.trimValue(this.CustomerAddressDto.StateProvince);
        this.CustomerAddressDto.PostalCode = this.trimValue(this.CustomerAddressDto.PostalCode);
      },
      trimValue(val) {
        //only trim if the value isn't undefined
        if (val) {
          return val.trim();
        }
        return val;
      },
      validateState(ref) {
        if (this.veeFields[ref] && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
          return !this.errors.has(ref);
        }
        return null;
      },
    },
  };
</script>
